/**
 * The New Patient form's cards area while its settings load or after they
 * fail to load. No fallback layout renders without settings: the form shows
 * only what the account configured (U6). NewLeadPage keeps Save disabled
 * until `status` is 'ready'.
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';

export type FormSettingsStatus = 'loading' | 'error' | 'ready';

export const FORM_LOADING_MESSAGE = 'Loading form…';

export const FORM_LOAD_FAILED_MESSAGE =
  "The form couldn't load. Select Try again. If this keeps happening, contact your Prospera administrator.";

export const FORM_RETRY_LABEL = 'Try again';

/** Shown when the existing patient's details fail to load; Save stays off. */
export const PATIENT_DETAILS_FAILED_MESSAGE =
  "This patient's details couldn't load, so the plan can't be saved yet. Select Try again. If this keeps happening, contact your Prospera administrator.";

export function FormSettingsGate({
  status,
  onRetry,
  retrying = false,
  children,
}: {
  status: FormSettingsStatus;
  onRetry: () => void;
  retrying?: boolean;
  children: ReactNode;
}) {
  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500" role="status">
        <Loader2 className="h-5 w-5 animate-spin" />
        {FORM_LOADING_MESSAGE}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <Alert variant="error">
        <div className="flex flex-col items-start gap-3">
          <span>{FORM_LOAD_FAILED_MESSAGE}</span>
          <Button type="button" variant="secondary" size="sm" onClick={onRetry} loading={retrying}>
            {FORM_RETRY_LABEL}
          </Button>
        </div>
      </Alert>
    );
  }

  return <>{children}</>;
}
